import Decimal from 'decimal.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Column, DataSheetGrid, keyColumn, textColumn } from 'react-datasheet-grid';
import 'react-datasheet-grid/dist/style.css';
import { useTranslation } from 'react-i18next';
import { useToast } from 'shared/components/ui/use-toast';
import colorsStore from 'shared/store/colorsStore';
import useThreadsStore from 'shared/store/threadsStore';
import { AssistantActionCreatePortfolioType } from 'types/AssistantTypes';
import { FinecoButton } from '../../FinecoButton';
import { parseIntlNumber } from 'utils/numberUtils';
import { formatItalianNumber, formatItalianPercentage } from 'utils/formatUtils';
import { buildManualPortfolioPayload, ManualPortfolioRow } from './manualPortfolioPayload';
import './excelPast.css';

type ExcelPastTwoColumnsProps = {
  messagePortfolioCast: AssistantActionCreatePortfolioType;
  onSubmit: (threadId: string, actionId: string, data: any) => Promise<void>;
};

type Row = ManualPortfolioRow;

const ExcelPastTwoColumns: React.FC<ExcelPastTwoColumnsProps> = ({ messagePortfolioCast, onSubmit }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [rowsToAdd, setRowsToAdd] = useState(1);

  const containerRef = useRef(null);
  const [isPesoMode, setIsPesoMode] = useState<boolean>(() => {
    return messagePortfolioCast?.action.input.by_weights ?? true;
  });

  const setHideTempStatusUpdate = useThreadsStore((state) => state.setHideTempStatusUpdate);

  const resetAction = useThreadsStore((state) => state.resetAction);
  const { t } = useTranslation();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [data, setData] = useState<Row[]>(() => {
    if (messagePortfolioCast?.action.input.products && messagePortfolioCast?.action.input.products.length > 0) {
      const byWeights = messagePortfolioCast?.action.input.by_weights ?? true;
      return messagePortfolioCast.action.input.products.map((product) => ({
        isin: product.isin,
        weight: byWeights
          ? formatItalianPercentage(Number(product.weight))
          : `${product.value}`,
      }));
    }
    return [{ isin: '', weight: '' }];
  });

  useEffect(() => {
    setHideTempStatusUpdate(true);
  }, []);

  const deleteRow = (rowIndex: number) => {
    setData((prevData) => {
      const newData = [...prevData];
      newData.splice(rowIndex, 1);

      if (newData.length === 0) {
        return [{ isin: '', weight: '' }];
      }

      return newData;
    });
  };

  const dataLengthRef = useRef(-1);
  useEffect(() => {
    dataLengthRef.current = data.length;
  }, [data]);

  const baseColumns: Column<Row>[] = [
    {
      ...keyColumn<Row, 'isin'>('isin', textColumn),
      title: 'ISIN',
    },
    {
      ...keyColumn<Row, 'weight'>('weight', textColumn),
      title: 'Peso / Controvalore',
    },
  ];

  const deleteColumn: Column<Row> = {
    title: '',
    // Must fit the delete button below: 16px icon + 10px padding each side
    // (content-box) = 36px. A narrower column overflows the grid and puts a
    // permanent horizontal scrollbar on classic-scrollbar systems.
    basis: 36,
    grow: 0,
    shrink: 0,
    minWidth: 36,
    maxWidth: 36,
    component: ({ rowIndex }) => {
      if (dataLengthRef.current === 1) return null;
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            height: '100%',
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              deleteRow(rowIndex);
            }}
            style={{
              background: 'transparent',
              boxSizing: 'content-box',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '10px',
              color: colorsConfig.finecoBlack,
              opacity: 1,
              transition: 'opacity 0.2s',
              outline: 'none',
              boxShadow: 'none',
              marginBottom: 10,
              width: 16,
              height: 16,
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = '0.7';
            }}
          >
            <svg
              style={{ width: '16px', height: '16px', flexShrink: 0 }}
              viewBox="3 3 18 18"
              fill="none"
              stroke="#00549F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      );
    },
    disabled: true,
  };

  const columns = useMemo(() => {
    return data.length === 1 ? baseColumns : [...baseColumns, deleteColumn];
    // return [...baseColumns, deleteColumn];
  }, [data.length]);

  const handleRowChange = (newData: Row[]) => {
    const processedData = newData.map((row) => {
      const upperIsin = row.isin?.toUpperCase() ?? null;

      let formattedWeight = row.weight;
      formattedWeight = formattedWeight ? formattedWeight.trim() : '';
      if (!isPesoMode && formattedWeight) {
        formattedWeight = formattedWeight.replace(/%/g, '');
      } else if (isPesoMode && formattedWeight && !formattedWeight.endsWith('%')) {
        const numValue = parseIntlNumber(formattedWeight);
        if (!isNaN(numValue)) {
          formattedWeight = `${numValue}%`;
        }
      }

      return {
        isin: upperIsin,
        weight: formattedWeight,
      };
    });

    setData(processedData);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const body = buildManualPortfolioPayload(data, isPesoMode);

    onSubmit(messagePortfolioCast.action.thread_id, messagePortfolioCast.action.id, body)
      .then(() => {
        resetAction(messagePortfolioCast.action.thread_id);
        setSubmitting(false);
        toast({
          title: t('global.success'),
          description: t('assistant.manualPortfolioSuccess'),
        });
        setHideTempStatusUpdate(false);
      })
      .catch((error) => {
        // The SDK's APIError carries the parsed backend body on `response`
        // ({ error, code } for validation failures); `message` is only the raw
        // "API request failed with status 400: {…}" envelope. Prefer the
        // human-readable backend message, falling back through the other
        // possible shapes before the generic copy.
        let errorMessage: string =
          error?.response?.error ??
          error?.response?.data?.error ??
          error?.response?.detail ??
          error?.data?.detail ??
          error?.message ??
          t('assistant.manualPortfolioError');
        if (errorMessage.includes('validation error')) {
          errorMessage = 'Errore di validazione. Controlla i tuoi dati';
        }
        setSubmitting(false);
        toast({
          title: t('global.error'),
          description: errorMessage,
          variant: 'destructive',
        });
      });
  };

  let allIsins = true;
  const healthyTW = () => {
    let tw = new Decimal(0);
    for (let i = 0; i < data.length; i++) {
      if (data[i].weight) {
        const w = data[i].weight;
        const p = parseIntlNumber(w);
        if (isNaN(p)) continue;
        const dec = new Decimal(p);
        tw = tw.add(dec);
      }
      if (!data[i].isin) {
        allIsins = false;
      }
    }
    return +tw.toFixed();
  };
  const tw = healthyTW();

  let isSubmitDisabled = false;

  data.forEach((row) => {
    if (row.weight && row.weight.trim() !== '' && (!row.isin || row.isin.trim() === '')) {
      allIsins = false;
    }
  });

  if (!allIsins) {
    isSubmitDisabled = true;
  }

  const hasInvalidValues = data.some((row) => {
    if (!row.weight || row.weight.trim() === '') return false;
    return isNaN(parseIntlNumber(row.weight));
  });

  if (hasInvalidValues) {
    isSubmitDisabled = true;
  }

  const hasAtLeastOneEntry = data.some(
    (row) =>
      row.isin &&
      row.isin.trim() !== '' &&
      row.weight &&
      row.weight.trim() !== '' &&
      !isNaN(parseIntlNumber(row.weight)),
  );

  if (!hasAtLeastOneEntry) {
    isSubmitDisabled = true;
  }
  if (tw > 100) {
    if (isPesoMode) {
      isSubmitDisabled = true;
    }
  }
  if (tw < 100) {
    if (isPesoMode) {
      isSubmitDisabled = true;
    }
  }

  useEffect(() => {
    const handleCellClick = (e) => {
      const cell = e.target.closest('.dsg-cell');
      if (cell) {
        const input = cell.querySelector('input');
        if (input) {
          setTimeout(() => {
            input.focus();
            input.select();
          }, 0);
        }
      }
    };

    const grid = document.querySelector('.custom-datasheet-wrapper');
    if (grid) {
      grid.addEventListener('click', handleCellClick);
    }

    return () => {
      if (grid) {
        grid.removeEventListener('click', handleCellClick);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="excelContainer">
      <div className="excelContainerInner">
        <div className="mainContent">
          <h2 className="excelContainerInnerTitle">{t('assistant.createManualPortfolio')}</h2>
          <ModeToggle isPesoMode={isPesoMode} setIsPesoMode={setIsPesoMode} />
        </div>
        <div className="custom-datasheet-wrapper">
          <div className="excelEditor">
            <div className="custom-datasheet-wrapper" style={{ maxWidth: 'calc(100% - 20px)' }}>
              <div className="datasheet-container">
                <div className="datasheet-body-wrapper">
                  <DataSheetGrid
                    key={data.length === 1 ? 'single' : 'multiple'}
                    value={data}
                    onChange={handleRowChange}
                    columns={columns}
                    rowHeight={56}
                    addRowsComponent={({ addRows }) => (
                      <div className="dsg-add-row">
                        <button className="dsg-add-row-btn" onClick={() => addRows(rowsToAdd)}>
                          {t('global.addRows')}
                        </button>
                        <input
                          className="dsg-add-row-input"
                          type="number"
                          min={1}
                          value={rowsToAdd}
                          onChange={(e) => setRowsToAdd(Number(e.target.value))}
                        />
                        <span style={{ marginLeft: 6 }}>{t('global.rows')}</span>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                position: 'absolute',
                bottom: 20,
                right: 10,
                padding: '2px 6px',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {isPesoMode ? `Totale: ${tw}%` : `Totale: ${formatItalianNumber(tw)}`}
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <FinecoButton
          click={handleSubmit}
          copy={submitting ? t('actions.submitting') : t('actions.submit')}
          disabled={isSubmitDisabled}
          testId="manual-portfolio-submit"
        />
      </div>
    </div>
  );
};

const ModeToggle = ({
  isPesoMode,
  setIsPesoMode,
}: {
  isPesoMode: boolean;
  setIsPesoMode: (isPesoMode: boolean) => void;
}) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
      }}
    >
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: `2px solid ${isPesoMode ? colorsConfig.main : '#ccc'}`,
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {isPesoMode && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: colorsConfig.main,
              }}
            />
          )}
        </span>
        <input
          type="radio"
          name="mode"
          checked={isPesoMode}
          onChange={() => setIsPesoMode(true)}
          style={{ display: 'none' }}
        />
        <span
          style={{
            color: isPesoMode ? colorsConfig.main : colorsConfig.finecoBlack,
          }}
        >
          Peso
        </span>
      </label>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: `2px solid ${!isPesoMode ? colorsConfig.main : '#ccc'}`,
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {!isPesoMode && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: colorsConfig.main,
              }}
            />
          )}
        </span>
        <input
          type="radio"
          name="mode"
          checked={!isPesoMode}
          onChange={() => setIsPesoMode(false)}
          style={{ display: 'none' }}
        />
        <span
          style={{
            color: !isPesoMode ? colorsConfig.main : colorsConfig.finecoBlack,
          }}
        >
          Controvalore
        </span>
      </label>
    </div>
  );
};

export default ExcelPastTwoColumns;
